import { StackLayout } from '@euler/app/components/StackLayout';
import { TouchCopyable } from '@euler/app/components/TouchCopyable';
import { Typography } from '@euler/app/components/typography';
import { useCurrentUser } from '@euler/app/flows/auth/context';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import Euler from '@euler/components/layout';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useInfiniteList } from '@euler/functions/useInfiniteList';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { OrgMemberAccountTxDetail } from '@euler/model/OrgMemberAccountTxDetail';
import { OrgMemberAccountTransaction } from '@euler/model/entity';
import { AccountTxCategory, AccountTxType } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { formatMoney } from '@euler/utils/formatMoney';
import { formatDateTime } from '@euler/utils/formatters';
import { memo, useCallback, useEffect } from 'react';
import {
  ActivityIndicator,
  FlatList,
  ListRenderItem,
  StyleProp,
  Text,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kLimit = 20;

const keyExtractor = (tx: OrgMemberAccountTransaction) => String(tx.id);

export const AccountTransactionsScreen = wrapNavigatorScreen(
  () => {
    const { bottom } = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const contentContainerStyle = useScreenContentContainerStyle();
    const profile = useCurrentUser();
    const memberId = profile?.user.memberId ?? 0;
    const { storeService } = useServiceFactory();

    const { data, status, fetch, fetchMore } = useInfiniteList({
      limit: kLimit,
      query: async offset => {
        return await storeService.getMemberAccountTransactions(
          memberId,
          undefined,
          offset,
          kLimit,
        );
      },
    });

    const renderItem: ListRenderItem<OrgMemberAccountTransaction> = useCallback(
      ({ item }) => {
        return <Cell tx={item} />;
      },
      [],
    );

    useEffect(() => {
      void fetch();
    }, [fetch]);

    return (
      <FlatList
        data={data}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        onEndReached={() => fetchMore()}
        contentContainerStyle={[
          {
            flexGrow: 1,
            padding: rpx(0),
            paddingBottom: bottom,
          },
          contentContainerStyle,
        ]}
        ListHeaderComponent={
          !status.loadedDataLength && status.isLoading ? (
            <Center
              css={`
                flex: 1;
              `}
            >
              <ActivityIndicator />
            </Center>
          ) : null
        }
        ListFooterComponent={
          status.loadedDataLength > 0 && status.isLoading ? (
            <ActivityIndicator />
          ) : null
        }
      />
    );
  },
  {
    title: '账户明细',
  },
);

const Cell = memo(
  ({
    tx,
    style,
  }: {
    tx: OrgMemberAccountTransaction;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const infoFontSize = rpx(12);
    const detail = tx.extra
      ? (JSON.parse(tx.extra) as OrgMemberAccountTxDetail)
      : undefined;
    const productReferral =
      detail?.type === 'productReferral' ||
      detail?.type === 'productReferralRevoke'
        ? detail.productReferral
        : undefined;
    const campaignReward =
      detail?.type === 'campaignReward'
        ? detail.campaignReward
        : detail?.type === 'campaignRewardShare'
        ? detail.campaignRewardShare
        : undefined;
    return (
      <StackLayout
        direction="row"
        style={style}
        justifyContent="space-between"
        alignItems="flex-start"
        css={`
          padding: ${rpx(10)}px ${rpx(15)}px;
        `}
      >
        <StackLayout>
          <TxTypeLabel type={tx.txType} />
          {productReferral != null && (
            <Euler.View mt={rpx(5)} mb={rpx(5)}>
              <Typography.Text size={infoFontSize}>
                车牌号码: {productReferral.licensePlateNo}
              </Typography.Text>
              <Typography.Text size={infoFontSize}>
                车型名称: {productReferral.vehicleName}
              </Typography.Text>
              <Typography.Text size={infoFontSize}>
                施工项目: {productReferral.subject}
              </Typography.Text>
              <Text>
                <Typography.Text size={infoFontSize}>
                  产品名称: {`${productReferral.productName}`}
                </Typography.Text>
                <Text
                  style={{
                    opacity: 0.5,
                    fontStyle: 'italic',
                    fontWeight: 'bold',
                  }}
                >{` x${productReferral.qty ?? 1}`}</Text>
              </Text>
            </Euler.View>
          )}
          {campaignReward != null && (
            <Euler.View mt={rpx(5)} mb={rpx(5)}>
              <Typography.Text size={infoFontSize}>
                活动名称: {campaignReward.campaignName}
              </Typography.Text>
              {campaignReward.licensePlateNo ? (
                <Typography.Text size={infoFontSize}>
                  车牌号码: {campaignReward.licensePlateNo}
                </Typography.Text>
              ) : null}
            </Euler.View>
          )}
          <TouchCopyable text={tx.id.toString()}>
            <Typography.Label color="#777" regular size={infoFontSize}>
              流水单号: {tx.id}
            </Typography.Label>
          </TouchCopyable>
          <Typography.Label color="#777" size={infoFontSize}>
            交易时间: {formatDateTime(tx.createdAt)}
          </Typography.Label>
        </StackLayout>

        <StackLayout alignItems="flex-end">
          <TxCategoryLabel category={tx.txCategory} />
          <Typography.Label size={rpx(12)} lineHeight={rpx(18)}>
            {tx.txCategory === AccountTxCategory.In ? '+' : '-'}
            {formatMoney(tx.amount)}
          </Typography.Label>
          <Typography.Label size={rpx(12)} lineHeight={rpx(18)}>
            余额: {formatMoney(tx.balance)}
          </Typography.Label>
        </StackLayout>
        <View
          css={`
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #ddd;
            height: 1px;
          `}
        />
      </StackLayout>
    );
  },
);

const kCategoryInfo: {
  [p in AccountTxCategory]: { text: string; color: string };
} = {
  [AccountTxCategory.In]: { text: '收入', color: StatusColors.Success },
  [AccountTxCategory.Out]: { text: '支出', color: StatusColors.Info },
};

const kTypeInfo: { [p in AccountTxType]: { text: string; color: string } } = {
  [AccountTxType.ProductReferring]: {
    text: '推荐奖励',
    color: StatusColors.Success,
  },
  [AccountTxType.ProductReferringShare]: {
    text: '推荐奖励 (服务顾问分成)',
    color: StatusColors.Success,
  },
  [AccountTxType.ProductAdoption]: {
    text: '施工奖励',
    color: StatusColors.Info,
  },
  [AccountTxType.ProductAdoptionShare]: {
    text: '施工奖励  (服务顾问分成)',
    color: StatusColors.Info,
  },

  [AccountTxType.RevokeProductAdoptionShare]: {
    text: '施工奖励  (服务顾问分成) - 撤销',
    color: StatusColors.Danger,
  },

  [AccountTxType.RevokeProductReferring]: {
    text: '推荐奖励 - 撤销',
    color: StatusColors.Danger,
  },
  [AccountTxType.RevokeProductReferringShare]: {
    text: '推荐奖励 (服务顾问分成) - 撤销',
    color: StatusColors.Danger,
  },
  [AccountTxType.RevokeProductAdoption]: {
    text: '施工奖励 - 撤销',
    color: StatusColors.Danger,
  },

  // 成交奖励 reads as a third, sales-side fee. Use Notice (warm) so it
  // doesn't collide with 推荐 (Success/green) or 施工 (Info/blue).
  [AccountTxType.ProductClosing]: {
    text: '成交奖励',
    color: StatusColors.Notice,
  },
  [AccountTxType.ProductClosingShare]: {
    text: '成交奖励 (服务顾问分成)',
    color: StatusColors.Notice,
  },
  [AccountTxType.RevokeProductClosing]: {
    text: '成交奖励 - 撤销',
    color: StatusColors.Danger,
  },
  [AccountTxType.RevokeProductClosingShare]: {
    text: '成交奖励 (服务顾问分成) - 撤销',
    color: StatusColors.Danger,
  },

  // Campaign reward is a fourth category, distinct from the three
  // product-commission kinds. Warm orange so it doesn't collide with
  // referring (green) / adoption (blue) / closing (Notice gold). Matches
  // the rewards feed body accent so the page and the feed read identically.
  [AccountTxType.CampaignReward]: { text: '活动奖励', color: '#fa8c16' },
  [AccountTxType.CampaignRewardShare]: {
    text: '活动奖励 (分成)',
    color: '#fa8c16',
  },
  [AccountTxType.RevokeCampaignReward]: {
    text: '活动奖励 - 撤销',
    color: StatusColors.Danger,
  },
  [AccountTxType.RevokeCampaignRewardShare]: {
    text: '活动奖励 (分成) - 撤销',
    color: StatusColors.Danger,
  },

  [AccountTxType.Withdraw]: { text: '余额提现', color: StatusColors.Notice },
  [AccountTxType.Refund]: { text: '账户退款', color: StatusColors.Warn },
};

const TxCategoryLabel = memo(
  ({
    category,
    style,
  }: {
    style?: StyleProp<ViewStyle>;
    category: AccountTxCategory;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        style={style}
        css={`
          background-color: ${kCategoryInfo[category]?.color ?? '#000'};
          padding: 0 ${rpx(5)}px;
          border-radius: ${rpx(3)}px;
        `}
      >
        <Typography.Label color="#fff" size={rpx(10)} lineHeight={rpx(18)}>
          {kCategoryInfo[category]?.text ?? category}
        </Typography.Label>
      </View>
    );
  },
);

const TxTypeLabel = memo(
  ({ type, style }: { style?: StyleProp<ViewStyle>; type: AccountTxType }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View style={style}>
        <Typography.Label
          color={kTypeInfo[type]?.color ?? '#000'}
          size={rpx(16)}
          lineHeight={rpx(18)}
          bold
        >
          {kTypeInfo[type]?.text ?? type}
        </Typography.Label>
      </View>
    );
  },
);
